import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { DatePipe } from '@angular/common';
import { HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  InjectionToken,
  LOCALE_ID,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { APP_SETTINGS, AppSettings } from 'src/app.settings';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { AppSettingsInitializer } from './config/app-settings-initializer';
import { AppSettingsStore } from './config/app-settings-store';
import { CustomRouteReuseStrategy } from './services/custom-route-reuse-strategy.service';
import { SUPPORTED_LOCALES } from './services/locale.service';
import { LoggingService } from './services/logging.service';
import { BASE_PATH } from './services/openapi';
import { CONSENT_PROVIDER } from './services/privacy/consent/consent-provider';
import { CookieYesConsentService } from './services/privacy/consent/cookie-yes-consent.service';

/** The language code from the `LOCALE_ID` */
export const LANGUAGE_ID = new InjectionToken<string>('LANGUAGE_ID');
/** The region code from the `LOCALE_ID` */
export const LOCALE_EXTENSION = new InjectionToken<string>('LOCALE_EXTENSION');

export const msalInterceptorFn: HttpInterceptorFn = (request, next) => {
  const store = inject(AppSettingsStore);
  if (!store.isLoaded) {
    // During startup, config may not be loaded yet. Do not instantiate
    // MsalInterceptor because it depends on MSAL_INTERCEPTOR_CONFIG,
    // which depends on APP_SETTINGS.
    return next(request);
  }
  return inject(MsalInterceptor).intercept(request, { handle: (r) => next(r) });
};

export const appConfig: ApplicationConfig = {
  providers: [
    AppSettingsStore,
    AppSettingsInitializer,
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([msalInterceptorFn])),
    provideNativeDateAdapter(),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    {
      provide: APP_SETTINGS,
      useFactory: (store: AppSettingsStore) => store.get(),
      deps: [AppSettingsStore],
    },
    provideAppInitializer(async () => {
      const initializer = inject(AppSettingsInitializer);
      await initializer.initialize();
    }),
    { provide: BASE_PATH, useFactory: (settings: AppSettings) => settings.BaseUrl, deps: [APP_SETTINGS] },
    { provide: LANGUAGE_ID, useFactory: (localeId: string) => localeId.split('-')[0], deps: [LOCALE_ID] },
    { provide: LOCALE_EXTENSION, useFactory: (localeId: string) => localeId.split('-')[1], deps: [LOCALE_ID] },
    { provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory, deps: [APP_SETTINGS, LOCALE_ID] },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory, deps: [APP_SETTINGS] },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory, deps: [APP_SETTINGS] },
    MsalInterceptor,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    LoggingService,
    { provide: ErrorHandler, useClass: ApplicationinsightsAngularpluginErrorService },
    DatePipe,
    { provide: CONSENT_PROVIDER, useExisting: CookieYesConsentService },
    provideClientHydration(withEventReplay()),
  ],
};

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (logLevel <= LogLevel.Error) {
    console.error(message);
  } else if (logLevel <= LogLevel.Warning) {
    console.warn(message);
  } else if (logLevel <= LogLevel.Info) {
    console.info(message);
  } else {
    console.log(message);
  }
}

export function MSALInstanceFactory(appSettings: AppSettings, localeId: string): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: appSettings.B2C.ClientId,
      authority: appSettings.B2C.SignUpSignInAuthority,
      knownAuthorities: [appSettings.B2C.AuthorityDomain],
      redirectUri: environment.production ? `/${localeId}/` : '/',
      postLogoutRedirectUri: environment.production ? `/${localeId}/` : '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(appSettings: AppSettings): MsalInterceptorConfiguration {
  const countryCodes = SUPPORTED_LOCALES.map((localeId) => localeId.split('-')[1]);
  const protectedResourceMap = new Map<string, string[]>([
    [`${appSettings.BaseUrl}/api/account/`, appSettings.Api.Scopes],
    [`${appSettings.BaseUrl}/api/account/*`, appSettings.Api.Scopes],
    [`${appSettings.BaseUrl}/api/profile/`, appSettings.Api.Scopes],
    [`${appSettings.BaseUrl}/api/profile/*`, appSettings.Api.Scopes],
    [`${appSettings.BaseUrl}/api/application/`, appSettings.Api.Scopes],
    [`${appSettings.BaseUrl}/api/application/*`, appSettings.Api.Scopes],
    [`${appSettings.BaseUrl}/api/performance-evaluations/`, appSettings.Api.Scopes],
    [`${appSettings.BaseUrl}/api/performance-evaluations/*`, appSettings.Api.Scopes],
    ...countryCodes.flatMap((code) => [
      [`${appSettings.BaseUrl}/api/${code}/portal/`, appSettings.Api.Scopes] as [string, string[]],
      [`${appSettings.BaseUrl}/api/${code}/portal/*`, appSettings.Api.Scopes] as [string, string[]],
    ]),
  ]);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(appSettings: AppSettings): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...appSettings.Api.Scopes],
    },
    loginFailedRoute: '/',
  };
}
